import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { repeat } from 'lit/directives/repeat.js';
import resetStyle from '../../shared/components/html-content/reset.style';
import { ludsBaseFont } from '../../shared/styles/typography';
import './toast';
import { LudsToast } from './toast';

export type ToastProps = Partial<LudsToast> & { index: number };

@customElement('luds-toast-stack')
export class LudsToastStack extends LitElement {
  static override styles = [
    resetStyle,
    ludsBaseFont,
    css`
      :host {
        --header-size: var(--spacing-spacing-64);
        --toast-position: var(--spacing-spacing-16);

        position: fixed;
        top: calc(var(--toast-position) + var(--header-size));
        right: var(--toast-position);

        display: flex;
        flex-direction: column-reverse;
        gap: var(--spacing-spacing-16); /* 16px */
        z-index: 1;
      }
    `,
  ];

  @state()
  toasts: ToastProps[] = [];

  addToast(toast: Omit<ToastProps, 'index'>): number {
    const index = this.toasts.length;
    this.toasts.push({ ...toast, index });
    this.requestUpdate();
    return index;
  }

  removeToast(index: number) {
    this.toasts = this.toasts.filter((toast) => toast.index !== index);
    this.requestUpdate();
  }

  protected override render() {
    return html`
      ${repeat(
        this.toasts,
        (toast) => toast.index,
        (toast) =>
          html`<luds-toast
            indexId=${toast.index}
            title=${ifDefined(toast.title)}
            message=${ifDefined(toast.message)}
            .actions=${toast.actions}
            variant=${ifDefined(toast.variant)}
            ?closable=${toast.closable}
            a11yCloseLabel=${ifDefined(toast.a11yCloseLabel)}
            .onClose=${toast.onClose}>
          </luds-toast>`
      )}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-toast-stack': LudsToastStack;
  }
}
